import React from 'react';
import Modal from '../Modal/Modal';
import { VerificationCodeInput } from '../VerificationCodeInput/VerificationCodeInput';
import styles from './VerificationCodeModal.module.scss';
import PrimaryButton from '../PrimaryButton/PrimaryButton';
import { formatPhoneNumber } from 'react-phone-number-input';
import { ErrorBanner } from '../ErrorBanner/ErrorBanner';
import {
    getMerchantsPhoneNumber,
    sendPhoneVerification,
    verifyPhone,
} from '../../shared/api/v2/customers/customers.utils';

type VerificationCodeModalProps = {
    isOpen: boolean;
    onClose: () => void;
    customerPhoneNumber: string;
    customerId: string | null;
    onConfirm?: () => void;
    initialError?: TVerificationModalError;
};

export type TVerificationModalError = {
    type: 'resend' | 'confirm' | 'general' | 'merchant_phone';
    message: string;
};
export const VerificationCodeModal: React.FC<VerificationCodeModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    initialError,
    customerPhoneNumber,
    customerId,
}) => {
    const [code, setCode] = React.useState('');
    const [isInitialErrorStale, setIsInitialErrorStale] = React.useState(false);
    const [error, setError] = React.useState<TVerificationModalError | null>(
        null
    );
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const handleConfirm = async () => {
        setIsSubmitting(true);
        const data = await verifyPhone({
            code,
            phone_number: customerPhoneNumber,
            customer_id: customerId || '',
        });
        if (data.error === 'phone_code_invalid') {
            setError({
                type: 'confirm',
                message: 'Invalid verification code.',
            });
            setIsSubmitting(false);
            return;
        }
        onConfirm?.();
        setCode('');
        onClose();
        setIsSubmitting(false);
    };

    const handleRefetchMerchantPhoneNumber = async () => {
        setIsSubmitting(true);
        const response = await getMerchantsPhoneNumber();
        if (response.error || !response.data) {
            setError({
                type: 'merchant_phone',
                message:
                    response.error || 'An error occurred. Please try again.',
            });
        } else {
            // we can safely assume that unsubscribe error needs to be shown since `merchant_phone` error can only happen
            // in a case where the phone number is not found and the user needs to resubscribe
            setError({
                type: 'general',
                message: `Text START to ${formatPhoneNumber(
                    response.data.phone_number
                )} to resubscribe to messages at any time.`,
            });
            setIsInitialErrorStale(true);
        }
        setIsSubmitting(false);
    };

    const handleResend = async () => {
        setIsSubmitting(true);
        const data = await sendPhoneVerification(customerPhoneNumber);
        if (data.error === 'phone_verified_too_many_times') {
            setError({
                type: 'resend',
                message:
                    'Too many verification codes have been sent. Please wait 24 hours before requesting another code',
            });
        }
        setIsSubmitting(false);
    };

    const handleOnCodeChange = (inputCode: string) => {
        if (error && error.type === 'confirm') {
            setError(null);
        }
        setCode(inputCode);
    };

    const rootError = isInitialErrorStale ? error : initialError || error;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Verification code"
            shouldFocusAfterRender={false}
        >
            <div>
                <p className={styles.Title}>Verification Code</p>
                <p className={styles.Description}>
                    Please enter the verification code we sent to the number{' '}
                    {formatPhoneNumber(customerPhoneNumber) || ''}
                </p>
                <VerificationCodeInput
                    isError={error?.type === 'confirm'}
                    code={code}
                    onChange={handleOnCodeChange}
                />
                {rootError && rootError.type !== 'confirm' ? (
                    <ErrorBanner
                        errorText={rootError.message}
                        errorComponent={
                            rootError.type === 'merchant_phone' ? (
                                <p>
                                    {
                                        rootError.message.split(
                                            'Please try again.'
                                        )[0]
                                    }
                                    <a
                                        className={styles.RefetchLink}
                                        onClick={
                                            handleRefetchMerchantPhoneNumber
                                        }
                                    >
                                        Please try again.
                                    </a>
                                </p>
                            ) : undefined
                        }
                    />
                ) : (
                    <button
                        className={styles.ResendButton}
                        onClick={handleResend}
                    >
                        Resend Code
                    </button>
                )}
                <div>
                    <PrimaryButton
                        isLoading={isSubmitting}
                        className={styles.ConfirmButton}
                        type="button"
                        onClick={handleConfirm}
                        disabled={isSubmitting || !!rootError}
                    >
                        Confirm
                    </PrimaryButton>
                </div>
            </div>
        </Modal>
    );
};
